import { getAnimationProperty } from '../../../animation/get-animation-property';
import { getCurrentAnimationBreakpoint } from '../../../animation/get-current-animation-breakpoint';
import { getMergedAnimationData } from '../../../animation/get-merged-animation-data';
import { extractClassFromSelector } from '../extract-class-from-selector';

const getClassNameFromAnimationEntry = (entryClass: string): string => {
  const normalizedClassName = extractClassFromSelector(entryClass);

  if (normalizedClassName) {
    return normalizedClassName;
  }

  const primarySelector = entryClass.trim().split(',')[0]?.trim() ?? '';

  return /^[A-Za-z0-9_-]+$/.test(primarySelector) ? primarySelector : '';
};

/**
 * Restore animation data attributes for an element from global animation data.
 *
 * @param {JQuery} $element Element to restore animation data for.
 */
export const restoreAnimationDataAttributes = ($element: JQuery): void => {
  // Check if element has animation data in global object
  if ('function' !== typeof window.et_has_animation_data || !window.et_has_animation_data($element)) {
    return;
  }

  // Get current breakpoint and create suffix for responsive properties
  const currentBreakpoint = getCurrentAnimationBreakpoint();
  const suffix = 'desktop' === currentBreakpoint ? '' : `_${currentBreakpoint}`;

  // Get merged animation data from global objects
  const mergedAnimationData = getMergedAnimationData();

  // Find matching animation entry
  const animationEntry = mergedAnimationData.find(entry => {
    if (!entry.class) {
      return false;
    }
    const className = getClassNameFromAnimationEntry(entry.class);

    if (!className) {
      return false;
    }

    return $element.hasClass(className);
  });

  let animationData = null;

  if (animationEntry) {
    const style = getAnimationProperty(animationEntry, 'style', suffix);
    const repeat = getAnimationProperty(animationEntry, 'repeat', suffix);
    const duration = getAnimationProperty(animationEntry, 'duration', suffix);
    const delay = getAnimationProperty(animationEntry, 'delay', suffix);
    const intensity = getAnimationProperty(animationEntry, 'intensity', suffix);
    const startingOpacity = getAnimationProperty(animationEntry, 'starting_opacity', suffix);
    const speedCurve = getAnimationProperty(animationEntry, 'speed_curve', suffix);

    // Only use if we have all required data
    if (style && repeat && duration && delay && intensity && startingOpacity && speedCurve) {
      animationData = {
        'data-animation-style': style,
        'data-animation-repeat': 'once' === repeat ? '' : 'infinite',
        'data-animation-duration': duration,
        'data-animation-delay': delay,
        'data-animation-intensity': intensity,
        'data-animation-starting-opacity': startingOpacity,
        'data-animation-speed-curve': speedCurve,
      };
    }
  }

  // Restore animation data attributes
  if (animationData) {
    $element.attr(animationData);
  }
};
